import { Controller } from 'stimulus'

import { createConsumer } from '@rails/actioncable'

import { diffJson } from 'diff'

export default class extends Controller {
  static classes = [
    'error'
  ]

  static targets = [
    'restUrl',
    'cableStatus',
    'cableReceived',
    'channel',
    'restOutput',
    'cableOutput']

  static values = {
    // Optional: omitted for a channel with no REST endpoint of its own, which mounts a second
    // instance of this controller alongside the one that fetches the resource.
    restUrl: String,
    cableUrl: String,
    // The channel identifier used to subscribe. Does not need to identify a specific
    // resource - the resource is identified in the `observe` message instead.
    channel: Object,
    // The resource's serial number, sent as part of the `observe` message payload.
    serialNumber: String,
    // The observer access token, sent as part of the `observe` message payload.
    accessToken: String,
    // Optional: the payload type to debug, ignoring every other message on the channel.
    messageType: String,
    cableReceived: {
      type: Number,
      default: 0
    }
  }

  connect () {
    this.channelTarget.textContent = JSON.stringify(this.channelValue, null, ' ')

    if (this.hasRestUrlValue) {
      this.restUrlTarget.textContent = this.restUrlValue

      // Fetched purely for display - no longer a precondition for connecting the cable.
      setTimeout(() => {
        this.fetchRest()
      }, 0)
    }

    setTimeout(() => {
      this.connectCable()
    }, 0)
  }

  async fetchRest () {
    const response = await fetch(this.restUrlValue)

    const data = await response.json()
    this.restOutputTarget.textContent = JSON.stringify(data, null, ' ')
    this.restOutputTarget.classList.toggle(this.errorClass, !response.ok)
  }

  connectCable () {
    // Create the consumer
    this.cableStatusTarget.textContent = 'Connecting...'
    const consumer = createConsumer(this.cableUrlValue)

    // Connect to the channel
    this.subscription = consumer.subscriptions.create(this.channelValue, {
      received: (data) => {
        if (this.messageTypeValue && data.type && data.type !== this.messageTypeValue) return

        this.cableReceivedValue++

        this.firstReceived ||= this.firstReceived || data
        this.previousReceived ||= this.previousReceived || data

        const fragment = this.buildDiffFragment(data)

        this.previousReceived = data

        this.cableStatusTarget.textContent = 'Streaming'
        this.cableReceivedTarget.textContent = this.cableReceivedValue
        this.cableOutputTarget.innerHTML = ''
        this.cableOutputTarget.appendChild(fragment)
      },

      // Called when the subscription is ready for use on the server. The server does not
      // start streaming until it receives a valid `observe` message.
      connected: () => {
        this.cableStatusTarget.textContent = 'Connected (observing...)'
        this.subscription.perform('observe', {
          serial_number: this.serialNumberValue,
          access_token: this.accessTokenValue
        })
        this.cableStatusTarget.classList.toggle(this.errorClass, false)
      },

      // Called when the subscription is disconnected by the server.
      disconnected: () => {
        this.cableStatusTarget.textContent = 'Disconnected'
        this.cableStatusTarget.classList.toggle(this.errorClass, true)
      },

      // Called when the subscription is rejected by the server.
      rejected: () => {
        this.cableStatusTarget.textContent = 'Rejected'
        this.cableStatusTarget.classList.toggle(this.errorClass, true)
      }
    })
  }

  // Renders the message, highlighting every line that differs from the first message received.
  buildDiffFragment (data) {
    const changedSincePrevious = this.changedLines(this.previousReceived, data)
    const fragment = document.createDocumentFragment()
    let lineNumber = 0

    diffJson(this.firstReceived, data).forEach((part) => {
      // Removed data is not shown, so it occupies no line of the rendered message.
      if (part.removed) return

      const lines = splitLines(part.value)

      lines.forEach((line, index) => {
        const span = document.createElement('span')

        if (part.added) {
          // Blue marks a line that still differs from the first message but has settled since the previous one.
          const highlight = changedSincePrevious.has(lineNumber + index) ? 'bg-primary' : 'bg-info'
          span.classList.add(highlight)
        }

        span.textContent = line
        fragment.appendChild(span)
      })

      lineNumber += lines.length
    })

    return fragment
  }

  // Returns the line numbers of `data` that were added or changed relative to `comparison`.
  changedLines (comparison, data) {
    const changed = new Set()
    let lineNumber = 0

    diffJson(comparison, data).forEach((part) => {
      if (part.removed) return

      const lines = splitLines(part.value)

      if (part.added) lines.forEach((_line, index) => changed.add(lineNumber + index))

      lineNumber += lines.length
    })

    return changed
  }
}

// Splits a diff part into its lines, each keeping its own trailing newline.
function splitLines (value) {
  return value.match(/[^\n]*\n|[^\n]+/g) || []
}
